import type { Viewer } from '@puppycad/cad-core';
import type * as THREE from 'three';
import type { Observable } from 'rxjs';

/**
 * 解析当前 Viewer 中的已选对象列表。
 *
 * 设计说明：
 * 1. `web-client` 只能通过 `Viewer` 的公开选择结果读取能力取数；
 * 2. 这里统一把 `selectedObjectIds -> scene object` 的映射收口到一个函数里，避免各个选择 store 继续探测 `entitySelectionManager`；
 * 3. 若场景尚未就绪或对象已经被销毁，直接返回空数组，保证上层状态可预测。
 *
 * @param viewer 当前 Viewer。
 * @returns 当前已选中的场景对象列表。
 */
export function resolveViewerSelectedObjects(viewer: Viewer | null): THREE.Object3D[] {
  if (!viewer) {
    return [];
  }

  const selectedObjectIds = viewer.getSelectedObjectIds();
  if (selectedObjectIds.length === 0) {
    return [];
  }

  return selectedObjectIds
    .map((objectId) => viewer.findSceneObjectById(objectId))
    .filter((object): object is THREE.Object3D => object !== null);
}

/**
 * 解析当前 Viewer 中第一个满足条件的已选对象。
 *
 * @template TObject 目标对象类型。
 * @param viewer 当前 Viewer。
 * @param predicate 类型守卫或筛选函数。
 * @returns 第一个满足条件的已选对象；未命中时返回 null。
 */
export function resolveViewerSelectedObject<TObject extends THREE.Object3D>(
  viewer: Viewer | null,
  predicate: (object: THREE.Object3D) => object is TObject,
): TObject | null {
  const selectedObjects = resolveViewerSelectedObjects(viewer);
  return selectedObjects.find(predicate) ?? null;
}

/**
 * 解析当前 Viewer 中第一个已选对象，不做类型过滤。
 *
 * @param viewer 当前 Viewer。
 * @returns 第一个已选对象；未命中时返回 null。
 */
export function resolveViewerPrimarySelectedObject(viewer: Viewer | null): THREE.Object3D | null {
  return resolveViewerSelectedObjects(viewer)[0] ?? null;
}

/**
 * 获取 Viewer 对外公开的实体交互事件流。
 *
 * 设计说明：
 * 1. 选择 store 只允许订阅 `Viewer.observeEntityInteraction()`；
 * 2. 若 Viewer 尚未就绪，返回 null，由上层自行决定是否延后订阅；
 * 3. 这样可以把 `entitySelectionManager.observe(...)` 从 `web-client` 主链中移除。
 *
 * @param viewer 当前 Viewer。
 * @param eventType 交互事件类型。
 * @returns 对应事件流；Viewer 未就绪时返回 null。
 */
export function observeViewerEntityInteraction(
  viewer: Viewer | null,
  eventType: PuppyCAD.EntityInteractionEventType,
): Observable<unknown> | null {
  return viewer?.observeEntityInteraction(eventType) ?? null;
}
